import { takeEvery, call, put, all, select } from "redux-saga/effects";
import {
  getVotes,
  createVote,
  updateVote,
  removeVote,
  getVoteOverride,
  updateVoteOverride,
} from "../api/resources/votes";
import { waitForNotificationDocumentId } from "../api/resources/notifications";
import { getMemberById } from "../api/resources/members";

import * as actions from "../state/votes/actions";
import * as notifyToasterContentActions from "../state/notify-toaster-content/actions";
import { NOTIFY_TOASTER_TYPE } from "../state/notify-toaster-content/reducer";
import i18next from "i18next";
import { isRequestSuccessful } from "../api/utils";

import { getMembers } from "../state/members/selectors";
import { handleCreateNotificationBySystem } from "./notifications";
import { notificationActionType, notificationType } from "../utils/constants";

function* initDeadlineVotes(action) {
  try {
    const [votes, override] = yield all([
      call(getVotes, action.payload),
      call(getVoteOverride, action.payload),
    ]);
    if (!isRequestSuccessful(votes) || !isRequestSuccessful(override)) {
      console.error("Init votes failed");
      console.error(votes);
      console.error(override);
    }

    let data = {
      votes: votes.data,
      officialVoteOverride: override.data,
    };

    yield put(actions.initVotesSucceeded(data));
  } catch (error) {
    console.error("Init votes failed");
    console.error(error);
  }
}

export function* fetchVotes(action) {
  try {
    const results = yield call(getVotes, action.payload);

    if (results.status === 200) {
      yield put(actions.getAllVotesSucceeded(results.data));
    } else {
    }
  } catch (error) {
    console.error(error);
  }
}

export function* fetchDeadlinesVotesStats(deadlines) {
  try {
    yield all(
      deadlines.map((deadline) => put(actions.getAllVotes(deadline.uri))),
    );
  } catch (error) {
    console.error(`Fetching deadline votes statistics failed -- ${error}`);
  }
}

// Every vote request flips updateVoteInProgress on. Only the *Succeeded actions
// used to flip it back, so one failed request left the yes/no/abstain radios
// disabled for the rest of the session. Each handler now reports failure.
function* handleVoteFailure(error) {
  console.error(`vote request failed - ${error}`);
  yield put(actions.voteRequestFailed(`${error}`));
  yield put(
    notifyToasterContentActions.setNotifyToasterContent({
      title: "Vote failed",
      // NotifyToaster renders `message` only, and notifyToaster.error reads
      // "Failed to send email" - the title never reaches the DOM.
      message: i18next.t("notifyToaster.voteFailed", { ns: "common" }),
      type: NOTIFY_TOASTER_TYPE.ERROR,
    }),
  );
}

export function* castVote(action) {
  try {
    const response = yield call(createVote, action.payload);
    if (!isRequestSuccessful(response)) throw new Error("cast vote failed");

    // yield call(waitForNotificationDocumentId, action.payload.deadlineUri);
    yield call(handleCreateNotificationBySystem, {
      payload: {
        ...response.data,
        type: notificationType.vote,
        eventType: notificationActionType.add,
        references: [
          {
            type: notificationType.deadline,
            uri: action.payload.deadlineUri,
          },
        ],
      },
    });

    yield put(actions.castNewVoteSucceeded(response.data));
  } catch (error) {
    yield call(handleVoteFailure, error);
  }
}

export function* updateExistingVote(action) {
  try {
    const response = yield call(updateVote, action.payload);
    if (!isRequestSuccessful(response)) throw new Error("update vote failed");

    yield call(handleCreateNotificationBySystem, {
      payload: {
        ...response.data,
        type: notificationType.vote,
        eventType: notificationActionType.update,
        references: [
          {
            type: notificationType.deadline,
            uri: action.payload.deadlineUri,
          },
        ],
      },
    });

    yield put(actions.updateVoteSucceeded(action.payload));
  } catch (error) {
    yield call(handleVoteFailure, error);
  }
}

export function* handleRemoveVote(action) {
  try {
    const response = yield call(removeVote, action.payload.uri);
    // isRequestSuccessful, not status === 200: a 204 on DELETE used to skip the
    // succeeded action and jam the in-progress flag on.
    if (!isRequestSuccessful(response)) throw new Error("remove vote failed");

    yield put(actions.removeVoteSucceeded(action.payload));
  } catch (error) {
    yield call(handleVoteFailure, error);
  }
}

function* handleOverrideOfficialVote(action) {
  try {
    const response = yield call(
      updateVoteOverride,
      action.payload.uri,
      action.payload.vote,
    );
    if (isRequestSuccessful(response)) {
      yield put(actions.overrideOfficialVoteSucceeded(response.data));
    }
  } catch (error) {
    console.error(error);
  }
}

// getMemberById hits a non-proxy endpoint, so a 401 on a single voter's
// profile would otherwise redirect the whole session to /unauthorized, and a
// rejection inside the yield all() below would abort every other voter too.
// tolerate401 stops the redirect; the catch turns any failure into a stub keyed
// by id so the voter still renders (no infinite loading spinner). A 401 is
// flagged forbidden so the UI shows the same gray "restricted access" box used
// for references, while still keeping the vote itself. Other (transient)
// errors are left un-flagged so they don't masquerade as restricted access.
function fetchVoteMemberSafe(userID) {
  return getMemberById(userID, { tolerate401: true }).catch((error) => ({
    data: { id: userID, forbidden: error?.response?.status === 401 },
  }));
}

export function* handleInitVotesMemberNames(action) {
  try {
    const members = yield select(getMembers);
    const actionVotes = action.payload;

    const votesWithoutMembers = actionVotes.filter(
      (v) => !v.member || typeof v.member === "undefined",
    );
    let updatedVotes = votesWithoutMembers.map((vote) => {
      const votingMember = members.find((m) => m.userID === vote.userID);
      if (votingMember) {
        return {
          ...vote,
          member: {
            name: votingMember.name,
            work: votingMember.work,
            photo: votingMember.photo,
          },
        };
      } else {
        return {
          ...vote,
          member: {
            loading: true,
          },
        };
      }
    });

    const votesReadyToShow = updatedVotes.filter((v) => !v.member.loading);
    const votesToLoad = updatedVotes.filter((v) => v.member.loading);
    yield all(
      votesReadyToShow.map((vote) =>
        put(
          actions.initVotesMemberNamesSucceeded({
            ...vote,
          }),
        ),
      ),
    );

    const missingNamesTasks = votesToLoad.map((v) =>
      fetchVoteMemberSafe(v.userID),
    );

    const missingMembers = yield all(missingNamesTasks);

    const loadedVotes = votesToLoad.map((vote) => {
      const votingMember = missingMembers.find(
        (m) => m.data.id === vote.userID,
      );

      if (votingMember) {
        return {
          ...vote,
          member: {
            name: votingMember.data.name,
            work: votingMember.data.company,
            photo: votingMember.data.profileImage,
            forbidden: votingMember.data.forbidden,
          },
        };
      }

      return vote;
    });

    yield all(
      loadedVotes.map((vote) =>
        put(actions.initVotesMemberNamesSucceeded({ ...vote })),
      ),
    );
  } catch (error) {
    console.error(`init votes member names failed - ${error}`);
  }
}

export default function* votesSaga() {
  yield takeEvery(actions.initDeadlineVotes, initDeadlineVotes);
  yield takeEvery(actions.GET_VOTES, fetchVotes);
  yield takeEvery(actions.CREATE_VOTE, castVote);
  yield takeEvery(actions.UPDATE_VOTE, updateExistingVote);
  yield takeEvery(actions.REMOVE_VOTE, handleRemoveVote);
  yield takeEvery(actions.overrideOfficialVote, handleOverrideOfficialVote);

  yield takeEvery(actions.initVotesMemberNames, handleInitVotesMemberNames);
}
